import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { useSelector } from "react-redux";
import { storage } from "../../../config/firebase";
import vehicleService from "../../../service/vehicle.service";
import {
  createPendingStorageId,
  emptyReboque,
  reboqueFormToTrailerCreatePayload,
  reboqueFormToTrailerUpdatePayload,
  trailerDocToReboqueForm,
  unwrapVehicleResponse,
} from "../../../util/vehicle/reboque-trailer-utils";
import {
  focusFirstInvalidField,
  validateReboqueForm,
} from "../validation";
import ReboqueFormFields from "./ReboqueFormFields";
import "../vehicle.css";

function normalizePersistedReboqueId(value) {
  const id = String(value || "").trim();
  if (!id || id === "undefined" || id.startsWith("embedded-")) {
    return "";
  }
  return id;
}

/**
 * Modal de cadastro/edição individual de reboque (vehicle com trailers: true).
 */
function QuickReboqueModal({
  open,
  onClose,
  reboqueId = null,
  onCreated,
  onSaved,
}) {
  const user = useSelector((state) => state.user);
  const savingRef = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;
  const loadSeqRef = useRef(0);
  const editingIdRef = useRef("");
  const pendingStorageIdRef = useRef("");

  const persistedReboqueId = normalizePersistedReboqueId(reboqueId);
  const isEditMode = Boolean(persistedReboqueId);

  const [form, setForm] = useState(emptyReboque);
  const [fieldErrors, setFieldErrors] = useState({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!open) {
      loadSeqRef.current += 1;
      return;
    }

    const seq = ++loadSeqRef.current;
    const idToEdit = normalizePersistedReboqueId(reboqueId);
    pendingStorageIdRef.current = idToEdit || createPendingStorageId();
    setFieldErrors({});
    setHasAttemptedSubmit(false);
    setSaving(false);
    setUploading(false);
    setErrorMsg("");
    savingRef.current = false;
    editingIdRef.current = idToEdit;

    if (!idToEdit) {
      setForm(emptyReboque());
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setForm(emptyReboque());

    (async () => {
      try {
        const response = await vehicleService.getById(idToEdit);
        if (cancelled || seq !== loadSeqRef.current || !openRef.current) return;
        const data = unwrapVehicleResponse(response);
        if (!data) {
          throw new Error("Reboque não encontrado.");
        }
        setForm(trailerDocToReboqueForm({ ...data, id: idToEdit }));
      } catch (error) {
        if (cancelled || seq !== loadSeqRef.current || !openRef.current) return;
        setErrorMsg(
          error?.response?.data?.message ||
            error?.message ||
            "Não foi possível carregar o reboque.",
        );
      } finally {
        if (!cancelled && seq === loadSeqRef.current) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, reboqueId]);

  useEffect(() => {
    if (!open || !hasAttemptedSubmit) return;
    const result = validateReboqueForm(form);
    setFieldErrors(result.success ? {} : result.fieldErrors);
  }, [open, hasAttemptedSubmit, form]);

  const handleUploadAntt = (file) => {
    if (!file) return;
    setUploading(true);
    const storageId =
      editingIdRef.current ||
      pendingStorageIdRef.current ||
      createPendingStorageId();
    pendingStorageIdRef.current = storageId;
    const storageRef = ref(storage, `vehicle/${storageId}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);
    uploadTask.on(
      "state_changed",
      () => {},
      () => {
        setUploading(false);
        if (openRef.current) setErrorMsg("Falha no upload do documento.");
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setUploading(false);
        if (!openRef.current) return;
        setForm((prev) => ({ ...prev, anttReboque: url }));
      },
    );
  };

  const handleSave = async () => {
    if (saving || uploading || loading || savingRef.current) return;
    setHasAttemptedSubmit(true);
    const validation = validateReboqueForm(form);
    if (!validation.success) {
      setFieldErrors(validation.fieldErrors);
      setErrorMsg(
        Array.isArray(validation.messages)
          ? validation.messages.join("\n")
          : "Preencha os campos obrigatórios do reboque.",
      );
      window.setTimeout(() => {
        focusFirstInvalidField(validation.firstFieldPath);
      }, 50);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setErrorMsg("");
    try {
      const editingId = normalizePersistedReboqueId(editingIdRef.current);
      if (editingId) {
        const payload = reboqueFormToTrailerUpdatePayload(
          form,
          user?.uidShipper || "",
        );
        const response = await vehicleService.update(editingId, payload);
        const saved = unwrapVehicleResponse(response);
        const asForm = saved
          ? trailerDocToReboqueForm({ ...saved, id: editingId })
          : { ...form, id: editingId };
        onSaved?.(saved || { ...asForm, id: editingId }, asForm);
      } else {
        const payload = reboqueFormToTrailerCreatePayload(
          form,
          user?.uidShipper || "",
        );
        const response = await vehicleService.create(payload);
        const created = unwrapVehicleResponse(response);
        if (!created?.id) {
          throw new Error("Resposta inválida ao criar reboque.");
        }
        const asForm = trailerDocToReboqueForm(created);
        onCreated?.(created, asForm);
      }
      onClose?.();
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message ||
          error?.message ||
          (isEditMode
            ? "Não foi possível atualizar o reboque."
            : "Não foi possível cadastrar o reboque."),
      );
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const isBusy = saving || uploading || loading;

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      aria-labelledby="quick-reboque-modal-title"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          width: "100%",
          maxWidth: { xs: "calc(100% - 24px)", sm: 720, md: 860 },
          mx: { xs: 1.5, sm: 2 },
        },
      }}
    >
      <Box
        className="new-vehicle-page quick-reboque-modal"
        sx={{ display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            pt: { xs: 2, sm: 2.5 },
            pb: 1.5,
            borderBottom: "1px solid rgba(1, 36, 67, 0.12)",
          }}
        >
          <Typography
            id="quick-reboque-modal-title"
            variant="h6"
            component="h2"
            sx={{
              color: "#012443",
              fontWeight: 700,
              fontSize: { xs: "1.05rem", sm: "1.2rem" },
              lineHeight: 1.3,
            }}
          >
            {isEditMode ? "Editar Reboque" : "Novo Reboque"}
          </Typography>
          <Typography
            sx={{
              color: "rgba(1,36,67,0.62)",
              fontSize: 14,
              mt: 0.5,
              lineHeight: 1.45,
            }}
          >
            {isEditMode
              ? "Altere os dados do reboque e salve para atualizar o cadastro."
              : "O cadastro é salvo independentemente do veículo. O vínculo ocorre ao confirmar o veículo."}
          </Typography>
        </Box>
        <DialogContent
          dividers
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: { xs: 2, sm: 2.25 },
            overflowX: "hidden",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 180,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              {errorMsg ? (
                <Typography
                  role="alert"
                  className="new-vehicle-field-error"
                  sx={{ mb: 2 }}
                >
                  {errorMsg}
                </Typography>
              ) : null}
              <ReboqueFormFields
                value={form}
                onChange={setForm}
                fieldErrors={fieldErrors}
                uploading={uploading}
                onUploadAntt={handleUploadAntt}
                disabled={saving}
              />
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            borderTop: "1px solid rgba(1, 36, 67, 0.12)",
          }}
        >
          <Button
            onClick={onClose}
            disabled={saving}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              minHeight: 40,
              borderRadius: "10px",
              color: "#012443",
              borderColor: "rgba(1, 36, 67, 0.28)",
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={isBusy}
            variant="contained"
            startIcon={
              saving ? <CircularProgress color="inherit" size={16} /> : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              minHeight: 40,
              borderRadius: "10px",
              backgroundColor: "#012443",
              "&:hover": { backgroundColor: "#023a6b" },
            }}
          >
            {saving
              ? "Salvando..."
              : isEditMode
                ? "Salvar Reboque"
                : "Cadastrar Reboque"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default QuickReboqueModal;
